import * as tracker from '@leroy-merlin-br/backpack-analytics';
import { useMutation } from '@tanstack/react-query';

import { Services } from '@/infra/services';
import { type Response as AddServicesToCartResponse } from '@/infra/services/checkout/cart/add-services-to-cart-by-id';
import { type AddedService } from '@/infra/services/checkout/model';
import { useUserResources } from '@/presenters/hooks/user';
import { cookies } from '@/utils/cookies';

interface UseAddServicesToCartById {
  onError?: () => void;
  onSuccess?: () => void;
}

const useAddServicesToCartById = (
  props: Readonly<UseAddServicesToCartById>
) => {
  const { updateCartItems } = useUserResources();
  const cartId = cookies.client.get('cartId')?.value || '';

  const { onError, onSuccess } = props;

  const pushAddToCart = (services: AddedService[]) => {
    if (!services.length) return;

    for (const service of services) {
      tracker.pushAddToCart({
        pageType: 'service',
        item: {
          id: service.serviceId,
          name: service.name,
          type: 'service',
          price: service.price.to,
          brand: 'Leroy Merlin',
          categoryTree: 'serviço',
          seller: 'Leroy Merlin',
          quantity: service.quantity,
        },
      });
    }
  };

  const result = useMutation({
    mutationFn: (
      data: Omit<
        Parameters<typeof Services.Checkout.Cart.addServicesToCartById>[0],
        'cartId'
      >
    ) =>
      Services.Checkout.Cart.addServicesToCartById({
        ...data,
        cartId: cartId!,
      }),
    onError,
    onSuccess: (response: AddServicesToCartResponse) => {
      const firstService = response.data[0];

      if (firstService) {
        updateCartItems(firstService.uniqueProductsCount);
      }

      pushAddToCart(response.data);

      onSuccess?.();
    },
  });

  return result;
};

export default useAddServicesToCartById;
